import React, { useState, useEffect } from 'react';
import { useAppStore } from '../store/useAppStore';
import { hapticFeedback } from '../utils/telegram';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icon';
import GlassSurface from './GlassSurface';
import './BottomNav.css';

interface NavItem {
  id: 'home' | 'operations' | 'ai_chat' | 'goals' | 'settings';
  icon: string;
  labelKey: string;
}

const navItems: NavItem[] = [
  { id: 'home', icon: 'home', labelKey: 'nav.home' },
  { id: 'operations', icon: 'format-list-bulleted', labelKey: 'nav.operations' },
  { id: 'ai_chat', icon: 'message-text', labelKey: 'nav.aiChat' },
  { id: 'goals', icon: 'target', labelKey: 'nav.goals' },
  { id: 'settings', icon: 'cog', labelKey: 'nav.settings' },
];

export const BottomNav: React.FC = () => {
  const { currentPage, setCurrentPage } = useAppStore();
  const { t } = useTranslation();
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  useEffect(() => {
    // Отслеживаем фокус на input/textarea для скрытия навигации
    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        setIsKeyboardVisible(true);
      }
    };

    const handleFocusOut = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        // Небольшая задержка, чтобы избежать мерцания при переходе между полями
        setTimeout(() => {
          if (document.activeElement?.tagName !== 'INPUT' && 
              document.activeElement?.tagName !== 'TEXTAREA') {
            setIsKeyboardVisible(false);
          }
        }, 100);
      }
    };

    // Отслеживаем изменение viewport через visualViewport API
    const handleViewportResize = () => {
      if (window.visualViewport) {
        const viewportHeight = window.visualViewport.height;
        const windowHeight = window.innerHeight;
        // Если viewport уменьшился более чем на 150px, считаем что клавиатура открыта
        setIsKeyboardVisible(windowHeight - viewportHeight > 150);
      }
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportResize);
    }

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportResize);
      }
    };
  }, []);

  const handleNavClick = (pageId: typeof currentPage) => {
    hapticFeedback.selection();
    setCurrentPage(pageId);
  };

  return (
    <nav className={`bottom-nav ${isKeyboardVisible ? 'bottom-nav--hidden' : ''}`}>
      <GlassSurface
        width="100%"
        height="100%"
        borderRadius={32}
        borderWidth={0.15}
        displace={8}
        distortionScale={-120}
        redOffset={5}
        greenOffset={12}
        blueOffset={20}
        brightness={65}
        opacity={0.75}
        blur={18}
        backgroundOpacity={0.05}
        saturation={1.8}
        xChannel="R"
        yChannel="G"
        mixBlendMode="overlay"
        style={{ 
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none'
        }}
      />
      {navItems.map((item) => {
        const isCenterButton = item.id === 'ai_chat';
        return (
          <button
            key={item.id}
            className={`bottom-nav__item ${
              currentPage === item.id ? 'bottom-nav__item--active' : ''
            } ${isCenterButton ? 'bottom-nav__item--center' : ''}`}
            onClick={() => handleNavClick(item.id)}
          >
            <span className="bottom-nav__icon">
              <Icon name={item.icon} size={isCenterButton ? 26 : 22} />
            </span>
            <span className="bottom-nav__label">{t(item.labelKey)}</span>
          </button>
        );
      })}
    </nav>
  );
};

